/**
 * GET /api/auth/profile — current authenticated user's profile.
 *
 * Schema is intentionally permissive: every field except `userId` is optional
 * because the onboarding flow renders profiles that haven't been assigned a
 * role/opcoId yet (the welcome page redirects them). Strict-on-shape would
 * cause the auth bootstrap to fail for partial profiles, breaking onboarding.
 *
 * Strict typing of role/email is delegated to the consuming code (e.g.
 * `<ProtectedRoute requireRole="admin">` checks `role === 'admin'` and
 * redirects when it isn't).
 */
import { z } from 'zod';

export const AuthProfileSchema = z.looseObject({
  userId: z.string().min(1).optional(),
  cognitoSub: z.string().min(1).optional(),
  sub: z.string().min(1).optional(),
  email: z.email().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  opcoId: z.string().nullable().optional(),
  opcoName: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  preferences: z.record(z.string(), z.unknown()).optional(),
  // Feature flags (feature-flags task, Phase 4): server-resolved flag map from
  // `resolveFlags(...)` (backend/src/lib/feature-flags.ts), added to
  // GET /api/auth/profile. Optional so an older backend response (or a
  // partial onboarding profile) without `flags` still validates.
  flags: z.record(z.string(), z.boolean()).optional(),
});

export type AuthProfile = z.infer<typeof AuthProfileSchema>;

/**
 * GET /api/auth/profile — backend currently returns either `{ profile }` (preferred)
 * or a bare profile object. Both shapes are accepted; consumer hooks normalize.
 */
export const ProfileResponseSchema = z.union([
  z.looseObject({ profile: AuthProfileSchema }),
  AuthProfileSchema,
]);

export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

/**
 * POST /api/auth/resolve — email-first login-method resolution (unified-auth
 * Phase 4, consumes the Phase 3 backend endpoint). The verdict is strictly
 * two-valued (`federate | local` — see unified-auth-architecture.md critique
 * C2): it is an existence-safe domain-bit decision and never a distinct
 * `signup` value, and it never reveals whether an account exists for the
 * submitted email.
 */
export const AuthResolveMethodSchema = z.enum(['federate', 'local']);

export const AuthResolveResponseSchema = z.object({
  method: AuthResolveMethodSchema,
});

export type AuthResolveMethod = z.infer<typeof AuthResolveMethodSchema>;
export type AuthResolveResponse = z.infer<typeof AuthResolveResponseSchema>;
